import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { api } from '../lib/api'
import { broadcastAuthChange } from '../lib/auth-broadcast'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Pre-fill from Vite env (DEV only — these vars must be left UNSET in
  // production). Set VITE_DEFAULT_EMAIL + VITE_DEFAULT_PASSWORD in
  // apps/web/.env.local to get a one-click login on localhost.
  const [email, setEmail] = useState<string>(
    import.meta.env.DEV ? (import.meta.env.VITE_DEFAULT_EMAIL ?? '') : '',
  )
  const [password, setPassword] = useState<string>(
    import.meta.env.DEV ? (import.meta.env.VITE_DEFAULT_PASSWORD ?? '') : '',
  )

  const loginMutation = useMutation({
    mutationFn: () => api.login(email, password),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'me'], data)
      broadcastAuthChange('login')
      notify.success(`Willkommen zurück, ${data.user.name}`)
      navigate({ to: data.user.startPage || '/' })
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    loginMutation.mutate()
  }

  return (
    <Card className="w-full max-w-md card-section">
      <CardHeader className="pt-7">
        <CardTitle className="font-serif text-3xl">Anmelden</CardTitle>
        <CardDescription>Bei deiner Ember-Instanz einloggen</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="dein@email.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="touch:h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Passwort</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="touch:h-11"
            />
          </div>
          <Button
            type="submit"
            className="w-full mt-2"
            size="lg"
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? 'Logge ein …' : 'Anmelden'}
          </Button>
          <p className="text-center text-sm text-text-secondary pt-1">
            Noch kein Konto?{' '}
            <Link to="/register" className="text-accent hover:underline">
              Erst-Setup
            </Link>
          </p>
        </form>
      </CardContent>
    </Card>
  )
}
